import React, { useState } from 'react';
import { Users, Trophy, Sparkles } from 'lucide-react';
import { WheelEntry, SpinHistory } from '../../types';
import { EntryItem } from './EntryItem';
import { AddEntryInput } from './AddEntryInput';
import { EntryToolbar } from './EntryToolbar';
import { WinnerHistoryList } from '../Winner/WinnerHistoryList';
import { getSegmentColor } from '../../utils/colors';

interface EntryPanelProps {
  entries: WheelEntry[];
  history: SpinHistory[];
  paletteId: string;
  onAddEntry: (label: string) => void;
  onBulkAdd: (labels: string[]) => void;
  onUpdateEntry: (id: string, updates: Partial<WheelEntry>) => void;
  onDeleteEntry: (id: string) => void;
  onDuplicateEntry: (label: string, color?: string) => void;
  onShuffle: () => void;
  onSortAZ: () => void;
  onDeduplicate: () => void;
  onClearAll: () => void;
  onDeleteHistoryItem: (id: string) => void;
  onClearHistory: () => void;
  isSpinning?: boolean;
}

export const EntryPanel: React.FC<EntryPanelProps> = ({
  entries,
  history,
  paletteId,
  onAddEntry,
  onBulkAdd,
  onUpdateEntry,
  onDeleteEntry,
  onDuplicateEntry,
  onShuffle,
  onSortAZ,
  onDeduplicate,
  onClearAll,
  onDeleteHistoryItem,
  onClearHistory,
  isSpinning = false,
}) => {
  const [activeTab, setActiveTab] = useState<'entries' | 'history'>('entries');

  return (
    <div
      className="glass-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 'clamp(0.75rem, 3vw, 1.25rem)',
        gap: '0.85rem',
        minHeight: 'auto',
        width: '100%',
      }}
    >
      {/* Header Tabs */}
      <div
        style={{
          display: 'flex',
          background: 'var(--bg-surface-elevated)',
          padding: '4px',
          borderRadius: '12px',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <button
          onClick={() => setActiveTab('entries')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '8px 12px',
            fontSize: '0.88rem',
            fontWeight: 600,
            borderRadius: '8px',
            background: activeTab === 'entries' ? 'var(--accent-primary)' : 'transparent',
            color: activeTab === 'entries' ? '#ffffff' : 'var(--text-secondary)',
          }}
        >
          <Users size={16} />
          <span>Entries ({entries.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('history')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '8px 12px',
            fontSize: '0.88rem',
            fontWeight: 600,
            borderRadius: '8px',
            background: activeTab === 'history' ? 'var(--accent-primary)' : 'transparent',
            color: activeTab === 'history' ? '#ffffff' : 'var(--text-secondary)',
          }}
        >
          <Trophy size={16} />
          <span>History ({history.length})</span>
        </button>
      </div>

      {/* Content Area */}
      {activeTab === 'entries' ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0.75rem' }}>
          {/* Quick Add Bar */}
          <AddEntryInput
            onAddEntry={onAddEntry}
            onBulkAdd={onBulkAdd}
            disabled={isSpinning}
          />

          {/* Actions Toolbar */}
          <EntryToolbar
            totalEntries={entries.length}
            onShuffle={onShuffle}
            onSortAZ={onSortAZ}
            onDeduplicate={onDeduplicate}
            onClearAll={onClearAll}
            disabled={isSpinning}
          />

          {/* List of Entries */}
          {entries.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2.5rem 1rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
                gap: '8px',
              }}
            >
              <Sparkles size={36} style={{ opacity: 0.4 }} />
              <p style={{ fontSize: '0.95rem', fontWeight: 600 }}>Your wheel is empty</p>
              <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                Add names above or paste a list to start spinning!
              </p>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                paddingRight: '4px',
                maxHeight: '440px',
              }}
            >
              {entries.map((entry, index) => {
                const segColor = getSegmentColor(index, entries.length, entry.color, paletteId);
                return (
                  <EntryItem
                    key={entry.id}
                    entry={entry}
                    index={index}
                    segmentColor={segColor}
                    onUpdate={onUpdateEntry}
                    onDelete={onDeleteEntry}
                    onDuplicate={(label, color) => onAddEntry(label)}
                    disabled={isSpinning}
                  />
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <WinnerHistoryList
          history={history}
          onDeleteHistoryItem={onDeleteHistoryItem}
          onClearHistory={onClearHistory}
          disabled={isSpinning}
        />
      )}
    </div>
  );
};
